import {
  Directive, ElementRef, HostListener, Input, OnDestroy,
  TemplateRef, ViewContainerRef, EmbeddedViewRef, Renderer2
} from '@angular/core';

@Directive({
  selector: '[sharedPopover]',
  standalone: false
})
export class SharedPopoverDirective implements OnDestroy {

  @Input('sharedPopover') content: string | null = null;
  @Input('sharedPopoverTemplate') template: TemplateRef<any> | null = null;
  @Input('sharedPopoverContext') context: any = null;

  private popoverEl: HTMLElement | null = null;
  private embeddedView: EmbeddedViewRef<any> | null = null;
  private isDestroyed = false;
  private outsideClickUnlisten: (() => void) | null = null;

  constructor(
    private el: ElementRef,
    private vcr: ViewContainerRef,
    private renderer: Renderer2
  ) {}

  // ── String mode — hover ──────────────────────────

  @HostListener('mouseenter')
  onMouseEnter() {
    if (!this.content || this.isDestroyed) return;
    if (this.popoverEl) return;

    const placement =
      this.el.nativeElement.getAttribute('placement')?.trim() || 'top';

    // Create container
    this.popoverEl = this.renderer.createElement('popover-container');

    // Classes
    this.renderer.addClass(this.popoverEl, 'info-popover');
    this.renderer.addClass(this.popoverEl, 'popover');
    this.renderer.addClass(this.popoverEl, 'show');

    const placementClassMap: Record<string, string[]> = {
      top: ['bs-popover-top', 'top', 'popover-top'],
      bottom: ['bs-popover-bottom', 'bottom', 'popover-bottom'],
      left: ['bs-popover-start', 'left', 'popover-left'],
      right: ['bs-popover-end', 'right', 'popover-right']
    };

    (placementClassMap[placement] || placementClassMap['top']).forEach(cls => {
      this.renderer.addClass(this.popoverEl!, cls);
    });

    this.renderer.setAttribute(this.popoverEl, 'role', 'tooltip');

    // Arrow
    const arrowEl = this.renderer.createElement('div');
    this.renderer.addClass(arrowEl, 'popover-arrow');
    this.renderer.addClass(arrowEl, 'arrow');
    this.renderer.setStyle(arrowEl, 'position', 'absolute');
    if (placement === 'top' || placement === 'bottom') {
      this.renderer.setStyle(arrowEl, 'left', '50%');
    } else if (placement === 'left' || placement === 'right') {
      this.renderer.setStyle(arrowEl, 'top', '50%');
    }
    // Content
    const contentEl = this.renderer.createElement('div');
    this.renderer.addClass(contentEl, 'popover-content');
    this.renderer.addClass(contentEl, 'popover-body');

    this.renderer.setProperty(contentEl, 'innerHTML', this.content);

    this.renderer.appendChild(this.popoverEl, arrowEl);
    this.renderer.appendChild(this.popoverEl, contentEl);

    // Ensure absolute positioning (Bootstrap style)
    this.renderer.setStyle(this.popoverEl, 'position', 'absolute');
    this.renderer.setStyle(this.popoverEl, 'zIndex', '9999');
    this.renderer.setStyle(this.popoverEl, 'pointerEvents', 'none');

    this.renderer.appendChild(document.body, this.popoverEl);

    // ── ✅ CORRECT POSITIONING ──────────────────────────

    const rect = this.el.nativeElement.getBoundingClientRect();
    const popoverRect = this.popoverEl.getBoundingClientRect();

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    let top = 0;
    let left = 0;

    if (placement === 'bottom') {
      top = rect.bottom + scrollTop + 8;
      left = rect.left + scrollLeft + (rect.width / 2) - (popoverRect.width / 2);
    }

    if (placement === 'top') {
      top = rect.top + scrollTop - popoverRect.height - 8;
      left = rect.left + scrollLeft + (rect.width / 2) - (popoverRect.width / 2);
    }

    if (placement === 'left') {
      top = rect.top + scrollTop + (rect.height / 2) - (popoverRect.height / 2);
      left = rect.left + scrollLeft - popoverRect.width - 8;
    }

    if (placement === 'right') {
      top = rect.top + scrollTop + (rect.height / 2) - (popoverRect.height / 2);
      left = rect.right + scrollLeft + 8;
    }

    this.renderer.setStyle(this.popoverEl, 'top', `${top}px`);
    this.renderer.setStyle(this.popoverEl, 'left', `${left}px`);
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (!this.content) return;
    this.removePopover();
  }

  // ── Template mode — click ──────────────────────────

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.template || this.isDestroyed) return;
    event.stopPropagation();

    if (this.popoverEl) {
      this.removePopover();
      return;
    }

    document.querySelectorAll('.shared-context-menu').forEach(el => el.remove());

    this.popoverEl = this.renderer.createElement('div');
    this.renderer.addClass(this.popoverEl, 'shared-context-menu');
    this.renderer.addClass(this.popoverEl, 'tabelcontextualmenu');
    this.renderer.setStyle(this.popoverEl, 'position', 'absolute');
    this.renderer.setStyle(this.popoverEl, 'zIndex', '9999');

    this.embeddedView = this.vcr.createEmbeddedView(this.template, this.context);
    this.embeddedView.detectChanges();
    this.embeddedView.rootNodes.forEach(node => this.renderer.appendChild(this.popoverEl, node));
    this.vcr.detach(this.vcr.indexOf(this.embeddedView));

    this.renderer.appendChild(document.body, this.popoverEl);

    const rect = this.el.nativeElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    this.renderer.setStyle(this.popoverEl, 'top', rect.bottom + scrollTop + 'px');
    this.renderer.setStyle(this.popoverEl, 'left', rect.left + scrollLeft - 150 + 'px');

    setTimeout(() => {
      if (!this.isDestroyed) {
        this.outsideClickUnlisten = this.renderer.listen('document', 'click', () => {
          this.removePopover();
        });
      }
    });
  }

  // ── Cleanup ──────────────────────────

  ngOnDestroy() {
    this.isDestroyed = true;
    this.removePopover();
  }

  private removePopover() {
    if (this.outsideClickUnlisten) {
      this.outsideClickUnlisten();
      this.outsideClickUnlisten = null;
    }

    if (this.popoverEl) {
      this.renderer.removeChild(document.body, this.popoverEl);
      this.popoverEl = null;
    }

    if (this.embeddedView) {
      this.embeddedView.destroy();
      this.embeddedView = null;
    }
  }
}